<template>
  <!-- ============================================================
       No.9 代行者権限付与：管理者側「代行者管理 一覧」
       採択者(status=10)起点で表示し、行展開で紐づく代行者を確認する。
       検索・ページング・ソート状態は agentSearch ストアで保持。
       ============================================================ -->
  <div class="CmsAgentList">
    <div class="contentsWidth mx-auto mt-4">
      <div>
        <p class="title mb-0">代行者管理 - 一覧</p>
        <hr class="title">
      </div>

      <!-- 絞り込み（報告管理一覧と同一項目・並び／ステータスは除く） -->
      <div id="serchWrap" class="mt-4">
        <div class="bold">絞り込み条件</div>
        <div class="search_area flex flexCenter">
          <div class="searchMiddle inlineBlock searchSpace">
            <div>報告番号</div>
            <div>
              <b-form-input v-model="fCode" placeholder="入力してください。"/>
            </div>
          </div>
          <div class="searchMiddle inlineBlock searchSpace">
            <div>報告者名</div>
            <div>
              <b-form-input v-model="fName" placeholder="入力してください。"/>
            </div>
          </div>
          <!-- No.9 代行者管理特有：代行者名でも絞り込み（一致する代行者を持つ報告者を表示） -->
          <div class="searchMiddle inlineBlock searchSpace">
            <div>代行者名</div>
            <div>
              <b-form-input
                v-model="fAgentName"
                :disabled="fHasAgent === 0"
                placeholder="入力してください。"/>
            </div>
          </div>
          <!-- No.9 代行者管理特有：代行者の設定有無で絞り込み（解除対象の棚卸し／未設定者の抽出用） -->
          <div class="searchShort inlineBlock searchSpace">
            <div>代行者設定</div>
            <div>
              <b-form-select v-model="fHasAgent" :options="hasAgentOptions">
                <template v-slot:first>
                  <option :value="null">-- 未選択 --</option>
                </template>
              </b-form-select>
            </div>
          </div>
          <div class="searchShort inlineBlock searchSpace">
            <div>年度</div>
            <div>
              <b-form-select v-model="fYear" :options="yearOptions">
                <template v-slot:first>
                  <option :value="'all'">すべて</option>
                </template>
              </b-form-select>
            </div>
          </div>
          <div class="searchMiddle inlineBlock">
            <div>助成種別</div>
            <div>
              <b-form-select v-model="fSeries" :options="seriesOptions">
                <template v-slot:first>
                  <option :value="null">-- 未選択 --</option>
                </template>
              </b-form-select>
            </div>
          </div>
        </div>
      </div>

      <!-- 一覧（採択者起点・1行=1採択者／行展開で代行者を表示） -->
      <div class="contentsWidth mt-3">
        <!-- ページング（報告管理一覧と同様） -->
        <div class="flex flex-between my-2">
          <b-pagination
            class="mb-1"
            aria-controls="agentTable"
            :value="currentPage"
            @input="setCurrentPage($event)"
            :total-rows="totalRows"
            :per-page="perPage"
          />
          <div>
            <div class="inlineBlock mr-2">
              {{ nowPageCount }}件 / 全{{ totalRows }}件
            </div>
            <div class="inlineBlock">
              <span class="mr-2">表示件数</span>
              <b-form-select
                class="per-page-select"
                :value="perPage"
                @change="setPerPage"
                :options="pageOptions"
              />
            </div>
          </div>
        </div>

        <b-table striped hover
          id="agentTable"
          table-class="cmsAgentTable"
          thead-class="tableHead"
          :items="filteredRows"
          :fields="header"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          show-empty>
          <template v-slot:empty>
            <div class="flex flexCenter">該当する採択者がありません。</div>
          </template>

          <!-- 報告者名：カナ(applicantKana)でソートし、表示は漢字名（報告管理一覧と同仕様） -->
          <template v-slot:cell(applicantKana)="row">
            {{ row.item.applicantName }}
          </template>

          <!-- 代行者：件数＋展開トグル -->
          <template v-slot:cell(agents)="row">
            <b-link @click="row.toggleDetails" class="agent-toggle">
              <span class="caret mr-1">{{ row.detailsShowing ? '▼' : '▶' }}</span>
              代行者 {{ row.item.agents.length }}名
            </b-link>
          </template>

          <template v-slot:cell(actions)="row">
            <b-link @click="goEdit(row.item.applicantId)">設定</b-link>
          </template>

          <!-- 行展開：紐づく代行者の明細 -->
          <template v-slot:row-details="row">
            <div class="agent-detail px-3 py-2">
              <div v-if="row.item.agents.length === 0" class="text-muted">
                代行者は登録されていません。「設定」から招待してください。
              </div>
              <table v-else class="detail-table">
                <thead>
                  <tr><th>氏名</th><th>メールアドレス</th><th>有効期限</th><th>状態</th></tr>
                </thead>
                <tbody>
                  <tr v-for="ag in row.item.agents" :key="ag.id"
                    :class="{ 'agent-hit': isAgentHit(ag) }">
                    <td>{{ ag.name }}</td>
                    <td>{{ ag.email }}</td>
                    <td>
                      <span v-if="ag.allowedTo">{{ formatTerm(ag.allowedTo) }}</span>
                      <span v-else class="text-muted">無期限</span>
                    </td>
                    <td>
                      <span v-if="ag.certified" class="badge badge-active">設定済</span>
                      <span v-else class="badge badge-pending">招待中</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';

export default {
  name: 'cmsAgentsList',
  data() {
    return {
      pageOptions: [
        { value: 1, text: 1 },
        { value: 10, text: 10 },
        { value: 20, text: 20 },
        { value: 50, text: 50 },
      ],
      hasAgentOptions: [
        { value: 1, text: 'あり' },
        { value: 0, text: 'なし' },
      ],
      // 申請/報告管理一覧と同仕様のソート：報告者名は「applicantKana でソート・表示は漢字名」（cell スロット）。
      // 代行者数は agents 配列の length で数値ソート（sortByFormatted）。
      header: [
        { key: 'code', label: '報告番号', sortable: true, thStyle: { width: '150px' } },
        { key: 'applicantKana', label: '報告者名', sortable: true, thStyle: { width: '160px' } },
        { key: 'year', label: '年度', sortable: true, thStyle: { width: '90px' } },
        { key: 'series', label: '助成種別', sortable: true, thStyle: { width: '170px' } },
        {
          key: 'agents',
          label: '代行者',
          sortable: true,
          sortByFormatted: (value, key, item) => item.agents.length,
          thStyle: { width: '120px' },
        },
        { key: 'actions', label: '操作', thStyle: { width: '90px' } },
      ],
      // 採択者起点の一覧（API取得）。1行=1採択者（採択が複数年度ある採択者は複数行）
      rows: [],
    };
  },
  computed: {
    // --- 検索条件は agentSearch ストアにバインド ---
    fCode: {
      get() { return this.$store.state.agentSearch.filter.code; },
      set(v) { this.$store.commit('agentSearch/setFilter', { key: 'code', value: v }); },
    },
    fName: {
      get() { return this.$store.state.agentSearch.filter.name; },
      set(v) { this.$store.commit('agentSearch/setFilter', { key: 'name', value: v }); },
    },
    fAgentName: {
      get() { return this.$store.state.agentSearch.filter.agentName; },
      set(v) { this.$store.commit('agentSearch/setFilter', { key: 'agentName', value: v }); },
    },
    fHasAgent: {
      get() { return this.$store.state.agentSearch.filter.hasAgent; },
      set(v) {
        this.$store.commit('agentSearch/setFilter', { key: 'hasAgent', value: v });
        // 「代行者設定=なし」と代行者名は同時に成立しないため名前条件をクリア
        if (v === 0) {
          this.$store.commit('agentSearch/setFilter', { key: 'agentName', value: '' });
        }
      },
    },
    fYear: {
      get() { return this.$store.state.agentSearch.filter.year; },
      set(v) { this.$store.commit('agentSearch/setFilter', { key: 'year', value: v }); },
    },
    fSeries: {
      get() { return this.$store.state.agentSearch.filter.series; },
      set(v) { this.$store.commit('agentSearch/setFilter', { key: 'series', value: v }); },
    },
    currentPage() {
      return this.$store.state.agentSearch.currentPage;
    },
    perPage() {
      return this.$store.state.agentSearch.perPage;
    },
    // ソート状態は agentSearch ストアに保持（画面遷移で維持・報告管理一覧と同作法）
    sortBy: {
      get() { return this.$store.state.agentSearch.sortBy; },
      set(v) { this.$store.commit('agentSearch/setSortBy', v); },
    },
    sortDesc: {
      get() { return this.$store.state.agentSearch.sortDesc; },
      set(v) { this.$store.commit('agentSearch/setSortDesc', v); },
    },
    // 絞り込みオプションは取得データから導出
    yearOptions() {
      const years = [...new Set(this.rows.map(r => r.year))].sort((a, b) => b - a);
      return years.map(y => ({ value: y, text: y }));
    },
    seriesOptions() {
      const list = [...new Set(this.rows.map(r => r.series))];
      return list.map(s => ({ value: s, text: s }));
    },
    filteredRows() {
      const code = (this.fCode || '').trim();
      const name = (this.fName || '').trim();
      const agentName = (this.fAgentName || '').trim();
      return this.rows
        .filter(
          r => (this.fYear === 'all' || r.year === this.fYear)
            && (this.fSeries === null || r.series === this.fSeries)
            && (code === '' || (r.code || '').includes(code))
            && (name === '' || (r.applicantName || '').includes(name))
            && (agentName === '' || r.agents.some(a => (a.name || '').includes(agentName)))
            && (this.fHasAgent === null
              || (this.fHasAgent === 1 ? r.agents.length > 0 : r.agents.length === 0)),
        )
        // 代行者名で絞り込んだ行は自動展開して一致した代行者を確認できるようにする
        .map(r => ({
          ...r,
          _showDetails: agentName !== '' && r.agents.some(a => (a.name || '').includes(agentName)),
        }));
    },
    totalRows() {
      return this.filteredRows.length;
    },
    nowPageCount() {
      if (this.totalRows === 0) {
        return 0;
      }
      const start = (this.currentPage - 1) * this.perPage;
      return Math.min(this.perPage, this.totalRows - start);
    },
  },
  methods: {
    async getAgents() {
      this.$store.dispatch('page/onLoading');
      const res = await api.send('/api/cms/agents/list').catch(() => false);
      if (res !== false) {
        this.rows = (res.data.agentList || []).map(r => ({
          applicantId: r.applicant_user_id,
          code: r.code,
          applicantName: r.applicant_name,
          // 報告者名のソート用キー（カナ順）。表示は applicantName（漢字）を cell スロットで出す
          applicantKana: r.applicant_kana,
          year: r.year,
          series: r.apptype_name,
          agents: (r.agents || []).map(a => ({
            id: a.agent_id,
            name: a.name,
            email: a.email,
            certified: a.is_certified,
            allowedTo: a.allowed_to,
          })),
        }));
        // 年度未選択（初回）のみ最新年度を初期選択（申請/選考/報告 各一覧と同作法＝yearList[0]）。
        // ユーザーが選び直した年度・「すべて」は agentSearch ストアに保持され上書きしない。
        if (this.fYear === null) {
          const years = [...new Set(this.rows.map(r => r.year))].sort((a, b) => b - a);
          if (years.length > 0) {
            this.$store.commit('agentSearch/setFilter', { key: 'year', value: years[0] });
          }
        }
      }
      this.$store.dispatch('page/offLoading');
    },
    // 代行者名で絞り込み中に、一致した代行者行をハイライトするか判定
    isAgentHit(ag) {
      const agentName = (this.fAgentName || '').trim();
      return agentName !== '' && (ag.name || '').includes(agentName);
    },
    // 有効期限の表示整形（終了日のみ）
    formatTerm(to) {
      return `${to} まで`;
    },
    setCurrentPage(page) {
      this.$store.commit('agentSearch/setCurrentPage', page);
    },
    setPerPage(perPage) {
      this.$store.commit('agentSearch/setPerPage', perPage);
    },
    goEdit(applicantId) {
      this.$router.push({ path: `/cms/agents/edit/${applicantId}` });
    },
  },
  created() {
    this.getAgents();
  },
};
</script>

<style scoped>
#serchWrap input, #serchWrap select {
  height: 50px;
}
.search_area {
  background: #DDD;
  padding: 10px;
  margin-bottom: 20px;
}
.searchMiddle {
  width: 205px;
}
.searchShort {
  width: 135px;
}
.searchSpace {
  margin-right: 15px;
  margin-bottom: 5px;
}
.per-page-select {
  width: 90px;
}
.agent-toggle {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.caret {
  color: #0A8EA7;
}
.agent-detail {
  background: #F4FAFB;
}
.detail-table {
  width: 100%;
  max-width: 960px;
}
.detail-table th, .detail-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #D5EAF0;
  text-align: left;
}
.detail-table th {
  font-size: 0.85rem;
  color: #555;
}
/* 列幅：氏名／メール（広め）／有効期限／状態 */
.detail-table th:nth-child(1), .detail-table td:nth-child(1) {
  width: 150px;
}
.detail-table th:nth-child(2), .detail-table td:nth-child(2) {
  width: auto;
  min-width: 340px;
  white-space: nowrap;
}
.detail-table th:nth-child(3), .detail-table td:nth-child(3) {
  width: 150px;
}
.detail-table th:nth-child(4), .detail-table td:nth-child(4) {
  width: 90px;
}
/* 代行者名で絞り込んだときに一致した代行者行を強調 */
.detail-table tr.agent-hit td {
  background: #FFF3CD;
  font-weight: bold;
}
.badge-active {
  background: #0A8EA7;
  color: #fff;
}
.badge-pending {
  background: #f0ad4e;
  color: #fff;
}
</style>
